import {
  type PurchaseDocumentType, normalizeText, type ExpenseImportItem } from '@alexiarest/shared';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  AdminOverview,
  PlatformExpense,
  PlatformExpenseInput,
  PlatformPnlResponse,
  PlatformRecurringExpense,
  PlatformRecurringExpenseInput,
  PlatformSettings,
  CashMovementInput,
  DailyRevenueInput,
  ExpenseInput,
  IngredientInput,
  InvestmentInput,
  InventoryCount,
  InventoryCountInput,
  MenuItemInput,
  Period,
  PosIntegrationPatch,
  PosIntegrationsResponse,
  PosIntegrationView,
  PosProvider,
  PosSyncResult,
  PosUnitsResult,
  RecipeInput,
  TargetInput,
  TenantSettings,
} from '@alexiarest/shared';
import { api, isDemoMode } from './client';
import { ApiError } from './error';
import { useLocationScope } from '../context/LocationContext';
import type {
  BatchResult,
  ConfirmRecipeProposalInput,
  ConfirmRecipeProposalResponse,
  IngredientBatch,
  RecipeBatch,
  RecipeProposalResponse,
  SupplierInput,
  ScanQuotaResponse,
  InventoryScanResult,
  ScanResult,
  HoldedReintento,
} from './contract';
import type {
  AmortizationRow,
  AssignableRole,
  AssistantChatRequest,
  AssistantChatResponse,
  AssistantSuggestion,
  CashFlowResponse,
  CashMovement,
  CostDeviation,
  DailyRevenue,
  DashboardResponse,
  ExpenseEntry,
  Ingredient,
  Investment,
  CustomRole,
  CustomRoleInput,
  InviteInput,
  KpisResponse,
  Location,
  MapeoVentasPropuesta,
  MeResponse,
  Membership,
  MenuItem,
  PayablesResponse,
  PricingAnalysis,
  PricingResponse,
  ProfitAndLoss,
  Recipe,
  RecipeCost,
  RecipeCostsResponse,
  ReconciliationResult,
  Supplier,
  Target,
  TargetComparison,
  TaxAllocation,
  TaxAllocationRequest,
  TeamResponse,
  Tenant,
  TenantUpdate,
  WeekdayPerformance,
} from './types';

/* ------------------------------------------------------------------ */
/* Claves de cache                                                     */
/* ------------------------------------------------------------------ */

export const keys = {
  me: ['me'] as const,
  settings: ['settings'] as const,
  ingredients: ['ingredients'] as const,
  suppliers: ['suppliers'] as const,
  recipes: ['recipes'] as const,
  recipe: (id: string) => ['recipe', id] as const,
  recipeCost: (id: string) => ['recipe-cost', id] as const,
  recipeCosts: ['recipe-costs'] as const,
  menuItems: ['menu-items'] as const,
  pricing: (locationId?: string) => ['pricing', locationId ?? 'all'] as const,
  platforms: (id: string) => ['platform-comparison', id] as const,
  deviations: ['cost-deviations'] as const,
  revenues: (period: Period, locationId?: string) => ['revenues', locationId ?? 'all', period.start, period.end] as const,
  expenses: (period: Period, locationId?: string) => ['expenses', locationId ?? 'all', period.start, period.end] as const,
  reconciliationCandidates: (invoiceId: string, locationId?: string) =>
    ['reconciliation-candidates', locationId ?? 'all', invoiceId] as const,
  payables: (today: string, locationId?: string) => ['payables', locationId ?? 'all', today] as const,
  investments: (locationId?: string) => ['investments', locationId ?? 'all'] as const,
  amortization: (month: string, locationId?: string) => ['amortization', locationId ?? 'all', month] as const,
  cashMovements: (period: Period, locationId?: string) => ['cash-movements', locationId ?? 'all', period.start, period.end] as const,
  target: (month: string, locationId?: string) => ['target', locationId ?? 'principal', month] as const,
  inventory: ['inventory'] as const,
  inventoryMonth: (month: string, locationId?: string) => ['inventory-month', locationId ?? 'all', month] as const,
  targetComparison: (month: string, locationId?: string) => ['target-comparison', locationId ?? 'all', month] as const,
  pnl: (period: Period, locationId?: string) => ['pnl', locationId ?? 'all', period.start, period.end] as const,
  pnlMonthly: (period: Period, locationId?: string) => ['pnl-monthly', locationId ?? 'all', period.start, period.end] as const,
  cashflow: (period: Period, locationId?: string) => ['cashflow', locationId ?? 'all', period.start, period.end] as const,
  kpis: (period: Period, locationId?: string) => ['kpis', locationId ?? 'all', period.start, period.end] as const,
  weekday: (period: Period, locationId?: string) => ['weekday', locationId ?? 'all', period.start, period.end] as const,
  dashboard: (period: Period, locationId?: string) => ['dashboard', locationId ?? 'all', period.start, period.end] as const,
  assistantSuggestions: (period: Period, locationId?: string) =>
    ['assistant-suggestions', locationId ?? 'all', period.start, period.end] as const,
  adminOverview: ['admin-overview'] as const,
  adminPnl: (year: number) => ['admin-pnl', year] as const,
  team: ['team'] as const,
  customRoles: ['team', 'roles'] as const,
  locations: ['locations'] as const,
  posIntegrations: ['pos-integrations'] as const,
};

function invalidateAll(qc: QueryClient, roots: string[][]): void {
  for (const key of roots) void qc.invalidateQueries({ queryKey: key });
}

/** Todo lo que depende del escandallo: recetas, ingredientes y precios. */
function invalidateCosting(qc: QueryClient): void {
  invalidateAll(qc, [
    ['ingredients'],
    ['recipes'],
    ['recipe'],
    ['recipe-cost'],
    ['menu-items'],
    ['pricing'],
    ['platform-comparison'],
    ['cost-deviations'],
    ['dashboard'],
  ]);
}

/** Todo lo que depende de los movimientos: P&L, tesoreria y KPIs. */
function invalidateFinance(qc: QueryClient): void {
  invalidateAll(qc, [
    ['revenues'],
    ['expenses'],
    ['reconciliation-candidates'],
    ['payables'],
    ['investments'],
    ['amortization'],
    ['cash-movements'],
    ['target'],
    ['target-comparison'],
    ['inventory'],
    ['inventory-month'],
    ['pnl'],
    ['pnl-monthly'],
    ['cashflow'],
    ['kpis'],
    ['weekday'],
    ['dashboard'],
  ]);
}

/* ------------------------------------------------------------------ */
/* Cuenta y ajustes                                                    */
/* ------------------------------------------------------------------ */

export function useMe(): UseQueryResult<MeResponse> {
  return useQuery({ queryKey: keys.me, queryFn: () => api.getMe() });
}

export function useLocations(): UseQueryResult<Location[]> {
  return useQuery({ queryKey: keys.locations, queryFn: () => api.listLocations() });
}

export function useCreateLocation(): UseMutationResult<Location, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.createLocation(name),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.locations });
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useUpdateLocation(): UseMutationResult<
  Location,
  Error,
  { locationId: string; name: string; active: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ locationId, ...input }) => api.updateLocation(locationId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.locations });
      void qc.invalidateQueries({ queryKey: keys.me });
      invalidateFinance(qc);
    },
  });
}

/**
 * Convierte el campo `photo` de una receta en un src pintable.
 * - dataURL (demo y fotos antiguas): se usa tal cual.
 * - clave de S3 ("photos/…"): se pide la URL firmada a la API y se cachea
 *   45 min (la firma dura 60: margen para no pintar firmas caducadas).
 */
export function usePhotoSrc(photo: string | undefined): string | undefined {
  const isKey = Boolean(photo?.startsWith('photos/'));
  const query = useQuery({
    queryKey: ['photo-url', photo],
    queryFn: () => api.getPhotoUrl(photo as string),
    enabled: isKey,
    staleTime: 45 * 60_000,
    gcTime: 60 * 60_000,
  });
  return isKey ? query.data : photo;
}

export function useUpdateTenant(): UseMutationResult<Tenant, Error, TenantUpdate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: TenantUpdate) => api.updateTenant(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

/** Tour guiado visto o saltado por este usuario: se refresca /me para no repetirlo. */
export function useMarkTourSeen(): UseMutationResult<{ tourSeenAt: string }, Error, void> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.markTourSeen(),
    // Se refresca /me pase lo que pase: si el servidor no lo ha guardado, el
    // tour volvera a salir y es mejor saberlo que creer que esta hecho.
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
    },
  });
}

export function useSettings(): UseQueryResult<TenantSettings> {
  return useQuery({ queryKey: keys.settings, queryFn: () => api.getSettings() });
}

export function useSaveSettings(): UseMutationResult<TenantSettings, Error, TenantSettings> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantSettings) => api.updateSettings(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.settings });
      invalidateCosting(qc);
      invalidateFinance(qc);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Ingredientes y proveedores                                          */
/* ------------------------------------------------------------------ */

export function useIngredients(): UseQueryResult<Ingredient[]> {
  return useQuery({ queryKey: keys.ingredients, queryFn: () => api.listIngredients() });
}

export function useSuppliers(): UseQueryResult<Supplier[]> {
  return useQuery({ queryKey: keys.suppliers, queryFn: () => api.listSuppliers() });
}

export interface SaveIngredientVars {
  id?: string;
  input: IngredientInput;
}

export function useSaveIngredient(): UseMutationResult<Ingredient, Error, SaveIngredientVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveIngredientVars) =>
      id ? api.updateIngredient(id, input) : api.createIngredient(input),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useDeleteIngredient(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteIngredient(id),
    onSuccess: () => invalidateCosting(qc),
  });
}

export interface SaveSupplierVars {
  id?: string;
  input: SupplierInput;
}

export function useSaveSupplier(): UseMutationResult<Supplier, Error, SaveSupplierVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveSupplierVars) =>
      id ? api.updateSupplier(id, input) : api.createSupplier(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.suppliers });
    },
  });
}

export function useDeleteSupplier(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteSupplier(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.suppliers });
    },
  });
}

/* ------------------------------------------------------------------ */
/* Recetas                                                             */
/* ------------------------------------------------------------------ */

export function useRecipes(): UseQueryResult<Recipe[]> {
  return useQuery({ queryKey: keys.recipes, queryFn: () => api.listRecipes() });
}

export function useRecipe(id: string | undefined): UseQueryResult<Recipe> {
  return useQuery({
    queryKey: keys.recipe(id ?? ''),
    queryFn: () => api.getRecipe(id as string),
    enabled: Boolean(id),
  });
}

/**
 * Un fallo de red o del servidor, frente a un problema de datos de la receta.
 *
 * El motor de costeo devuelve sus errores como 400 con el motivo escrito:
 * esos NO se reintentan, porque volver a pedirlo da exactamente lo mismo. Un
 * 500, un 429 o un corte de red si: ahi la receta no tiene nada malo.
 */
export function esFalloPasajero(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.code === 'NETWORK' || error.code === 'INTERNAL' || error.status >= 500;
  }
  // Sin ApiError no sabemos que ha pasado; tratarlo como pasajero solo cuesta
  // un reintento y evita marcar "Revisar" en una receta sana.
  return true;
}

export function useRecipeCost(id: string | undefined): UseQueryResult<RecipeCost> {
  return useQuery({
    queryKey: keys.recipeCost(id ?? ''),
    queryFn: () => api.getRecipeCost(id as string),
    enabled: Boolean(id),
    retry: (intentos, error) => intentos < 2 && esFalloPasajero(error),
    retryDelay: (intentos) => 400 * 2 ** intentos,
  });
}

/**
 * Coste de todas las recetas en una sola peticion.
 *
 * Sustituye a un `useRecipeCost` por fila en la tabla de Recetas: cien filas
 * eran cien peticiones a la vez, y las que se caian pintaban "Revisar" sin
 * motivo en recetas correctas.
 */
export function useRecipeCosts(): UseQueryResult<RecipeCostsResponse> {
  return useQuery({
    queryKey: keys.recipeCosts,
    queryFn: () => api.getRecipeCosts(),
    retry: (intentos, error) => intentos < 2 && esFalloPasajero(error),
    retryDelay: (intentos) => 400 * 2 ** intentos,
  });
}

export interface SaveRecipeVars {
  id?: string;
  input: RecipeInput;
}

export function useSaveRecipe(): UseMutationResult<Recipe, Error, SaveRecipeVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveRecipeVars) =>
      id ? api.updateRecipe(id, input) : api.createRecipe(input),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useProposeRecipePhoto(): UseMutationResult<RecipeProposalResponse, Error, Blob> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (file: Blob) => api.proposeRecipeFromFile(file), onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }) });
}

export function useProposeRecipe(): UseMutationResult<RecipeProposalResponse, Error, string> {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (text: string) => api.proposeRecipe(text), onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }) });
}

export function useTranscribeRecipeAudio(): UseMutationResult<
  { text: string },
  Error,
  { audioBase64: string; mimeType: string }
> {
  return useMutation({ mutationFn: (input) => api.transcribeRecipeAudio(input) });
}

export function useConfirmRecipeProposal(override?: (input: ConfirmRecipeProposalInput) => Promise<ConfirmRecipeProposalResponse>): UseMutationResult<
  ConfirmRecipeProposalResponse,
  Error,
  ConfirmRecipeProposalInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      if (override) return override(input);
      if (!input.draftId) return api.confirmRecipeProposal(input);
      const draft = await api.confirmDraft(input.draftId, { recipe: input });
      if (!draft.result?.recipeId) throw new Error('La receta no se ha confirmado.');
      const recipe = await api.getRecipe(draft.result.recipeId);
      const ingredients = await api.listIngredients();
      return { recipe, newIngredients: ingredients.filter((item) => draft.result?.ingredientIds?.includes(item.ingredientId)) };
    },
    onSuccess: () => { invalidateCosting(qc); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}

export function useDeleteRecipe(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteRecipe(id),
    onSuccess: () => invalidateCosting(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Carta y precios                                                     */
/* ------------------------------------------------------------------ */

export function useMenuItems(): UseQueryResult<MenuItem[]> {
  return useQuery({ queryKey: keys.menuItems, queryFn: () => api.listMenuItems() });
}

/** Precios de toda la carta; el menu engineering, con las unidades del local seleccionado. */
export function usePricing(): UseQueryResult<PricingResponse> {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: keys.pricing(locationId), queryFn: () => api.getPricing(locationId) });
}

export function usePlatformComparison(
  id: string | undefined,
): UseQueryResult<PricingAnalysis[]> {
  return useQuery({
    queryKey: keys.platforms(id ?? ''),
    queryFn: () => api.getPlatformComparison(id as string),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useCostDeviations(): UseQueryResult<CostDeviation[]> {
  return useQuery({ queryKey: keys.deviations, queryFn: () => api.getCostDeviations() });
}

export interface SaveMenuItemVars {
  id?: string;
  input: MenuItemInput;
}

export function useSaveMenuItem(): UseMutationResult<MenuItem, Error, SaveMenuItemVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: SaveMenuItemVars) =>
      id ? api.updateMenuItem(id, input) : api.createMenuItem(input),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useSaveMenuItemUnits(): UseMutationResult<
  MenuItem,
  Error,
  { id: string; unitsSold: number }
> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ id, unitsSold }) => api.updateMenuItemUnitsSold(id, unitsSold, writeLocationId),
    onSuccess: () => invalidateCosting(qc),
  });
}

export function useDeleteMenuItem(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteMenuItem(id),
    onSuccess: () => invalidateCosting(qc),
  });
}

/** "Aceptar coste actual": la referencia pasa a ser el coste de hoy. */
export function useAcceptMenuItemCost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.acceptMenuItemCost(id),
    // Aceptar un coste cambia la desviacion, el margen y el semaforo: hay que
    // refrescar TODO lo que cuelga del escandallo. Antes solo invalidaba
    // 'menu-items' y una clave inexistente ('menu'), asi que la tabla de
    // Precios y la tarjeta de recosteo se quedaban igual hasta recargar.
    onSuccess: () => invalidateCosting(queryClient),
  });
}

/* ------------------------------------------------------------------ */
/* Ingresos diarios                                                    */
/* ------------------------------------------------------------------ */

export function useRevenues(period: Period): UseQueryResult<DailyRevenue[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.revenues(period, locationId),
    queryFn: () => api.listRevenues(period, locationId),
  });
}

export function useSaveRevenue(): UseMutationResult<DailyRevenue, Error, DailyRevenueInput> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: (input: DailyRevenueInput) =>
      api.saveRevenue({ ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useDeleteRevenue(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: (date: string) => api.deleteRevenue(date, writeLocationId),
    onSuccess: () => invalidateFinance(qc),
  });
}

/**
 * Importacion en lote desde el TPV: un upsert por dia, en serie para no
 * saturar la API. Devuelve cuantos dias se han guardado. Si un dia falla,
 * se corta ahi: lo ya guardado queda guardado y el error dice en que fecha
 * se quedo, para poder reintentar sin miedo (el upsert es idempotente).
 */
export function useImportRevenues(): UseMutationResult<number, Error, DailyRevenueInput[]> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: async (days: DailyRevenueInput[]) => {
      let saved = 0;
      for (const day of days) {
        try {
          await api.saveRevenue({ ...day, locationId: writeLocationId, importSource: 'excel' });
          saved++;
        } catch (error) {
          const reason = error instanceof Error ? error.message : 'error desconocido';
          throw new Error(
            `Se guardaron ${saved} de ${days.length} días. Falló el ${day.date}: ${reason}. ` +
              'Puedes volver a importar el mismo archivo: los días ya guardados se sobrescriben igual.',
          );
        }
      }
      return saved;
    },
    onSettled: () => invalidateFinance(qc),
  });
}

/** Un plato de la carta con sus unidades vendidas nuevas. */
export interface UnitsImportItem {
  item: MenuItem;
  units: number;
}

/** Importacion en lote de unidades vendidas (modo "unidades" del importador). */
export function useImportUnitsSold(): UseMutationResult<number, Error, UnitsImportItem[]> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: async (rows: UnitsImportItem[]) => {
      let saved = 0;
      for (const { item, units } of rows) {
        try {
          await api.updateMenuItemUnitsSold(item.menuItemId, units, writeLocationId);
          saved++;
        } catch (error) {
          const reason = error instanceof Error ? error.message : 'error desconocido';
          throw new Error(
            `Se guardaron ${saved} de ${rows.length} platos. Falló «${item.name}»: ${reason}.`,
          );
        }
      }
      return saved;
    },
    onSettled: () => {
      invalidateAll(qc, [['menu-items'], ['pricing'], ['dashboard'], ['kpis']]);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Gastos                                                              */
/* ------------------------------------------------------------------ */

export function useExpenses(period: Period): UseQueryResult<ExpenseEntry[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.expenses(period, locationId),
    queryFn: () => api.listExpenses(period, locationId),
  });
}

export function useReconciliationCandidates(invoiceId: string | null): UseQueryResult<ExpenseEntry[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.reconciliationCandidates(invoiceId ?? 'none', locationId),
    queryFn: () => api.listReconciliationCandidates(invoiceId!, locationId),
    enabled: Boolean(invoiceId),
  });
}

export function usePayables(today: string): UseQueryResult<PayablesResponse> {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: keys.payables(today, locationId), queryFn: () => api.getPayables(today, locationId) });
}

export interface SaveExpenseVars {
  id?: string;
  input: ExpenseInput;
}

export function useSaveExpense(override?: (input: ExpenseInput) => Promise<ExpenseEntry>): UseMutationResult<ExpenseEntry, Error, SaveExpenseVars> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ id, input }: SaveExpenseVars) =>
      override ? override({ ...input, locationId: input.locationId ?? writeLocationId }) : id
        ? api.updateExpense(id, { ...input, locationId: input.locationId ?? writeLocationId })
        : api.createExpense({ ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useReconcileExpense(): UseMutationResult<
  ReconciliationResult,
  Error,
  { invoiceId: string; deliveryNoteIds: string[]; marcarFacturaPagada?: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceId, deliveryNoteIds, marcarFacturaPagada }) =>
      api.reconcileExpense(invoiceId, deliveryNoteIds, marcarFacturaPagada),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useUnreconcileExpense(): UseMutationResult<
  { invoiceId: string; deliveryNotes: number },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invoiceId: string) => api.unreconcileExpense(invoiceId),
    onSuccess: () => invalidateFinance(qc),
  });
}

export interface ImportExpensesVars {
  items: ExpenseImportItem[];
  /** Dar de alta los proveedores que el archivo trae y no existen. */
  createSuppliers: boolean;
}

/**
 * Importacion de gastos por archivo (7 sep 2026). Primero los proveedores
 * nuevos (para que cada gasto salga ya enlazado), luego los gastos en tandas
 * de 500. Si una tanda falla, se dice cuantos han entrado: volver a importar
 * el mismo archivo los duplicaria, asi que mejor revisar antes.
 */
export interface ImportExpensesResult {
  saved: number;
  /** Filas que ya estaban guardadas y no se han vuelto a crear. */
  repetidos: number;
}

export function useImportExpenses(): UseMutationResult<ImportExpensesResult, Error, ImportExpensesVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ items, createSuppliers }: ImportExpensesVars) => {
      const created = new Map<string, string>();
      if (createSuppliers) {
        const names = [...new Set(items.map((i) => i.newSupplierName).filter((n): n is string => !!n))];
        for (const name of names) {
          const supplier = await api.createSupplier({ name });
          created.set(normalizeText(name), supplier.supplierId);
        }
      }
      const inputs = items.map((i) => ({
        ...i.input,
        supplierId:
          i.input.supplierId ?? (i.newSupplierName ? created.get(normalizeText(i.newSupplierName)) : undefined),
      }));
      let saved = 0;
      let repetidos = 0;
      const TANDA = 500;
      for (let start = 0; start < inputs.length; start += TANDA) {
        try {
          const { created: n, skipped } = await api.importExpenses(inputs.slice(start, start + TANDA));
          saved += n;
          repetidos += skipped;
        } catch (error) {
          const reason = error instanceof Error ? error.message : 'error desconocido';
          throw new Error(
            `Se importaron ${saved} de ${inputs.length} gastos antes de fallar: ${reason}. ` +
              'Puedes volver a subir el mismo archivo: los que llevan número de documento no se duplican.',
          );
        }
      }
      return { saved, repetidos };
    },
    onSettled: () => {
      invalidateFinance(qc);
      invalidateAll(qc, [['suppliers']]);
    },
  });
}

/* --- Escaner de facturas --- */

export function useScanQuota(): UseQueryResult<ScanQuotaResponse> {
  return useQuery({ queryKey: ['scan-quota'], queryFn: () => api.getScanQuota() });
}

/**
 * Sube la foto y la manda a leer. Devuelve la propuesta de gasto; NO crea
 * nada: guardarlo es un `useSaveExpense` aparte, con lo que el usuario haya
 * corregido en la pantalla.
 */
/** Sube el documento al S3 del escaner y devuelve su clave. */
async function subirDocumento(blob: Blob): Promise<string> {
  const contentType = blob.type || 'image/jpeg';
  const { key, uploadUrl, fields } = await api.createScanUploadUrl(contentType);
  const body = new FormData();
  for (const [name, value] of Object.entries(fields)) body.append(name, value);
  body.append('file', blob);
  const res = await fetch(uploadUrl, { method: 'POST', body });
  if (!res.ok) throw new Error('No hemos podido subir el documento. Inténtalo otra vez.');
  return key;
}

/**
 * Hoja de recuento rellenada a mano -> cantidades propuestas (mejora 40).
 * NO guarda el recuento: eso lo hace `useSaveInventoryCount` con lo que el
 * usuario haya revisado en pantalla.
 */
export function useScanInventorySheet(): UseMutationResult<InventoryScanResult, Error, Blob> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (blob: Blob) => {
      if (isDemoMode) return api.scanInventorySheet('demo');
      return api.scanInventorySheet(await subirDocumento(blob));
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['scan-quota'] }); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}

export interface ScanVars {
  blob: Blob;
  /** Que se escanea: el aviso de duplicado se busca entre documentos del mismo tipo. */
  documentType?: PurchaseDocumentType;
}

export function useScanReceipt(): UseMutationResult<ScanResult, Error, ScanVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ blob, documentType }: ScanVars) => {
      // En la demo no hay S3 ni OCR: se salta la subida y el adaptador
      // devuelve una factura de ejemplo. Asi el escaparate recorre la MISMA
      // pantalla y el mismo motor de reglas que la app real.
      if (isDemoMode) return api.scanReceipt('demo', documentType);

      const contentType = blob.type || 'image/jpeg';
      const { key, uploadUrl, fields } = await api.createScanUploadUrl(contentType);
      const body = new FormData();
      for (const [name, value] of Object.entries(fields)) body.append(name, value);
      body.append('file', blob);
      const res = await fetch(uploadUrl, {
        method: 'POST',
        body,
      });
      if (!res.ok) throw new Error('No hemos podido subir el documento. Inténtalo otra vez.');
      return api.scanReceipt(key, documentType);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['scan-quota'] }); void qc.invalidateQueries({ queryKey: ['drafts'] }); },
  });
}

export function useDeleteExpense(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteExpense(id),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Inversiones                                                         */
/* ------------------------------------------------------------------ */

export function useInvestments(): UseQueryResult<Investment[]> {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: keys.investments(locationId), queryFn: () => api.listInvestments(locationId) });
}

export function useAmortizationSchedule(month: string): UseQueryResult<AmortizationRow[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.amortization(month, locationId),
    queryFn: () => api.getAmortizationSchedule(month, locationId),
  });
}

export interface SaveInvestmentVars {
  id?: string;
  input: InvestmentInput;
}

export function useSaveInvestment(): UseMutationResult<Investment, Error, SaveInvestmentVars> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ id, input }: SaveInvestmentVars) =>
      id
        ? api.updateInvestment(id, { ...input, locationId: input.locationId ?? writeLocationId })
        : api.createInvestment({ ...input, locationId: writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useDeleteInvestment(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteInvestment(id),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Movimientos de caja                                                 */
/* ------------------------------------------------------------------ */

export function useCashMovements(period: Period): UseQueryResult<CashMovement[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.cashMovements(period, locationId),
    queryFn: () => api.listCashMovements(period, locationId),
  });
}

export function useSaveCashMovement(): UseMutationResult<
  CashMovement,
  Error,
  CashMovementInput
> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: (input: CashMovementInput) => api.createCashMovement({ ...input, locationId: writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Objetivos                                                           */
/* ------------------------------------------------------------------ */

/**
 * Objetivo del mes del local seleccionado. Se EDITA por local, asi que en
 * consolidado se lee el del que escribiria (principal): la pantalla no
 * ensenya el formulario en ese caso.
 */
export function useTarget(month: string): UseQueryResult<Target | null> {
  const { locationId, writeLocationId } = useLocationScope();
  const efectivo = locationId ?? writeLocationId;
  return useQuery({
    queryKey: keys.target(month, efectivo),
    queryFn: () => api.getTarget(month, efectivo),
  });
}

/** Objetivo frente a real: por local, o consolidado con «Todos los locales». */
export function useTargetComparison(month: string): UseQueryResult<TargetComparison[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.targetComparison(month, locationId),
    queryFn: () => api.getTargetComparison(month, locationId),
  });
}

/* ------------------------------------------------------------------ */
/* Inventario                                                          */
/* ------------------------------------------------------------------ */

export function useInventoryCounts(): UseQueryResult<InventoryCount[]> {
  return useQuery({ queryKey: keys.inventory, queryFn: () => api.listInventoryCounts() });
}

export function useInventoryCount(month: string): UseQueryResult<InventoryCount> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.inventoryMonth(month, locationId),
    queryFn: () => api.getInventoryCount(month, locationId),
  });
}

export interface SaveInventoryVars {
  month: string;
  input: InventoryCountInput;
}

export function useSaveInventoryCount(): UseMutationResult<
  InventoryCount,
  Error,
  SaveInventoryVars
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, input }: SaveInventoryVars) => api.saveInventoryCount(month, input),
    onSuccess: () => invalidateFinance(qc),
  });
}

export function useDeleteInventoryCount(): UseMutationResult<
  void,
  Error,
  { month: string; locationId?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, locationId }) => api.deleteInventoryCount(month, locationId),
    onSuccess: () => invalidateFinance(qc),
  });
}

export interface SaveTargetVars {
  month: string;
  input: TargetInput;
}

export function useSaveTarget(): UseMutationResult<Target, Error, SaveTargetVars> {
  const qc = useQueryClient();
  const { writeLocationId } = useLocationScope();
  return useMutation({
    mutationFn: ({ month, input }: SaveTargetVars) =>
      api.saveTarget(month, { ...input, locationId: input.locationId ?? writeLocationId }),
    onSuccess: () => invalidateFinance(qc),
  });
}

/* ------------------------------------------------------------------ */
/* Analitica                                                           */
/* ------------------------------------------------------------------ */

export function useDashboard(period: Period): UseQueryResult<DashboardResponse> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.dashboard(period, locationId),
    queryFn: () => api.getDashboard(period, locationId),
  });
}

export function usePnl(period: Period): UseQueryResult<ProfitAndLoss> {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: keys.pnl(period, locationId), queryFn: () => api.getPnl(period, locationId) });
}

export function useMonthlyPnl(period: Period): UseQueryResult<ProfitAndLoss[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.pnlMonthly(period, locationId),
    queryFn: () => api.getMonthlyPnl(period, locationId),
  });
}

export function useCashFlow(period: Period): UseQueryResult<CashFlowResponse> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.cashflow(period, locationId),
    queryFn: () => api.getCashFlow(period, 0, locationId),
  });
}

export function useKpis(period: Period): UseQueryResult<KpisResponse> {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: keys.kpis(period, locationId), queryFn: () => api.getKpis(period, locationId) });
}

export function useWeekday(period: Period): UseQueryResult<WeekdayPerformance[]> {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: keys.weekday(period, locationId), queryFn: () => api.getWeekday(period, locationId) });
}

export function useTaxAllocation(): UseMutationResult<
  TaxAllocation[],
  Error,
  TaxAllocationRequest
> {
  return useMutation({ mutationFn: (input: TaxAllocationRequest) => api.allocateTax(input) });
}

/* ------------------------------------------------------------------ */
/* Asistente de IA                                                     */
/* ------------------------------------------------------------------ */

/**
 * Preguntas sugeridas del periodo. No se reintenta: si el asistente no esta
 * disponible, la pantalla lo dice una vez y no insiste.
 */
export function useAssistantSuggestions(
  period: Period,
): UseQueryResult<AssistantSuggestion[]> {
  const { locationId } = useLocationScope();
  return useQuery({
    queryKey: keys.assistantSuggestions(period, locationId),
    queryFn: () => api.getAssistantSuggestions(period, locationId),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

export function useAssistantChat(): UseMutationResult<
  AssistantChatResponse,
  Error,
  AssistantChatRequest
> {
  return useMutation({
    mutationFn: (input: AssistantChatRequest) => api.chatWithAssistant(input),
    retry: false,
  });
}

export interface MapeoVentasVars {
  columnas: string[];
  muestra: string[][];
  destinos: Array<{ key: string; label: string }>;
  /** Ventas del TPV (por defecto) o gastos. */
  tipo?: 'ventas' | 'gastos';
}

/**
 * Le pide a Alexia que diga que es cada columna del archivo que se acaba de
 * subir. Es una propuesta: se pinta en el mapeo y el usuario la corrige.
 */
export function useMapeoVentas(): UseMutationResult<
  MapeoVentasPropuesta,
  Error,
  MapeoVentasVars
> {
  return useMutation({
    mutationFn: (input: MapeoVentasVars) => api.proponerMapeoVentas(input),
    retry: false,
  });
}

/* ------------------------------------------------------------------ */
/* Facturacion                                                         */
/* ------------------------------------------------------------------ */

/**
 * Facturas de la suscripcion. `staleTime` alto: la lista cambia una vez al mes
 * y cada consulta es una llamada a Holded.
 */
export function useInvoices() {
  return useQuery({
    queryKey: ['invoices'],
    queryFn: () => api.getInvoices(),
    staleTime: 5 * 60_000,
  });
}

export function useCheckout(): UseMutationResult<{ url: string }, Error, 'basic' | 'pro'> {
  return useMutation({ mutationFn: (plan: 'basic' | 'pro') => api.startCheckout(plan) });
}

export function useFounderCheckout(): UseMutationResult<{ url: string }, Error, string> {
  return useMutation({ mutationFn: (code: string) => api.startFounderCheckout(code) });
}

export function useChangePlan(): UseMutationResult<{ ok: true }, Error, 'basic' | 'pro'> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (plan: 'basic' | 'pro') => api.changePlan(plan),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      // El plan lo fija el webhook y puede tardar un par de segundos mas que
      // nuestra respuesta: segunda pasada para que la pantalla lo recoja.
      setTimeout(() => void qc.invalidateQueries({ queryKey: ['me'] }), 4000);
    },
  });
}

export function useBillingPortal(): UseMutationResult<{ url: string }, Error, void> {
  return useMutation({ mutationFn: () => api.openBillingPortal() });
}

/** Fija la cantidad de asientos extra. `extraSeats` lo escribe el webhook. */
export function useSetSeats(): UseMutationResult<{ ok: true }, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quantity: number) => api.setExtraSeats(quantity),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.team });
      // El webhook puede tardar un par de segundos mas que nuestra respuesta.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: keys.me });
        void qc.invalidateQueries({ queryKey: keys.team });
      }, 4000);
    },
  });
}

/** Fija la cantidad de centros extra. `extraLocations` lo escribe el webhook. */
export function useSetExtraLocations(): UseMutationResult<{ ok: true }, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (quantity: number) => api.setExtraLocations(quantity),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.me });
      void qc.invalidateQueries({ queryKey: keys.locations });
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: keys.me });
        void qc.invalidateQueries({ queryKey: keys.locations });
      }, 4000);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Equipo                                                              */
/* ------------------------------------------------------------------ */

export function useTeam(enabled = true): UseQueryResult<TeamResponse> {
  return useQuery({ queryKey: keys.team, queryFn: () => api.getTeam(), enabled });
}

function invalidateTeam(qc: QueryClient): () => void {
  return () => {
    void qc.invalidateQueries({ queryKey: keys.team });
    void qc.invalidateQueries({ queryKey: keys.me });
  };
}

export function useInviteMember(): UseMutationResult<
  { membership: Membership },
  Error,
  InviteInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteInput) => api.inviteMember(input),
    onSuccess: invalidateTeam(qc),
  });
}

export function useChangeMemberRole(): UseMutationResult<
  { membership: Membership },
  Error,
  { userId: string; role: AssignableRole; customRoleId?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, role, customRoleId }) =>
      api.changeMemberRole(userId, role, customRoleId),
    onSuccess: invalidateTeam(qc),
  });
}

/* --- Roles personalizados (mejora 70) --- */

export function useCustomRoles(enabled = true): UseQueryResult<CustomRole[]> {
  return useQuery({
    queryKey: keys.customRoles,
    queryFn: async () => (await api.listCustomRoles()).roles,
    enabled,
  });
}

function invalidateRoles(qc: QueryClient): () => void {
  return () => {
    void qc.invalidateQueries({ queryKey: keys.customRoles });
    // Cambiar un rol cambia lo que ve quien lo lleva, empezando por uno mismo.
    void qc.invalidateQueries({ queryKey: keys.team });
    void qc.invalidateQueries({ queryKey: keys.me });
  };
}

export function useSaveCustomRole(): UseMutationResult<
  { role: CustomRole },
  Error,
  { roleId?: string; input: CustomRoleInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, input }) =>
      roleId ? api.updateCustomRole(roleId, input) : api.createCustomRole(input),
    onSuccess: invalidateRoles(qc),
  });
}

export function useDeleteCustomRole(): UseMutationResult<{ ok: true }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) => api.deleteCustomRole(roleId),
    onSuccess: invalidateRoles(qc),
  });
}

export function useMemberStatus(): UseMutationResult<
  { membership: Membership },
  Error,
  { userId: string; accion: 'desactivar' | 'reactivar' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, accion }) =>
      accion === 'desactivar' ? api.deactivateMember(userId) : api.activateMember(userId),
    onSuccess: invalidateTeam(qc),
  });
}

/** Contrata o da de baja el modulo escaner. El addon lo fija el webhook. */
export function useAddon(): UseMutationResult<
  { ok: true },
  Error,
  { addon: 'escaner'; accion: 'contratar' | 'cancelar' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ addon, accion }) =>
      accion === 'contratar' ? api.contractAddon(addon) : api.cancelAddon(addon),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me'] });
      void qc.invalidateQueries({ queryKey: ['receipts'] });
      // Igual que el plan: lo fija el webhook con un par de segundos de margen.
      setTimeout(() => {
        void qc.invalidateQueries({ queryKey: ['me'] });
        void qc.invalidateQueries({ queryKey: ['receipts'] });
      }, 4000);
    },
  });
}

/* ------------------------------------------------------------------ */
/* Importacion masiva del catalogo                                     */
/* ------------------------------------------------------------------ */

/**
 * Sube el catalogo en tandas: la API acepta 200 por peticion, y un fichero
 * de 600 ingredientes no puede fallar entero por eso.
 */
const TAMANYO_TANDA = 200;

export function useImportIngredients(override?: (batch: IngredientBatch) => Promise<BatchResult>): UseMutationResult<BatchResult, Error, IngredientBatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (batch: IngredientBatch) => {
      if (override) return override(batch);
      const total: BatchResult = { creados: 0, actualizados: 0, errores: [] };
      for (let i = 0; i < batch.items.length; i += TAMANYO_TANDA) {
        const parcial = await api.importIngredients({
          // Los proveedores solo en la primera tanda: ya quedan creados.
          suppliers: i === 0 ? batch.suppliers : undefined,
          items: batch.items.slice(i, i + TAMANYO_TANDA),
        });
        total.creados += parcial.creados;
        total.actualizados += parcial.actualizados;
        total.errores.push(
          ...parcial.errores.map((e) => ({ ...e, index: e.index + i })),
        );
      }
      return total;
    },
    onSuccess: () => {
      invalidateCosting(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

/** Una receta del lote con su posicion en el fichero, para no perderla. */
interface RecetaEnTanda {
  item: RecipeBatch['items'][number];
  indice: number;
}

/**
 * Sube recetas en tandas de 200 y devuelve las que han fallado, con su
 * posicion original.
 */
async function enviarRecetas(lote: RecetaEnTanda[]): Promise<{
  creados: number;
  actualizados: number;
  fallidos: Array<RecetaEnTanda & { name: string; message: string }>;
}> {
  const out = {
    creados: 0,
    actualizados: 0,
    fallidos: [] as Array<RecetaEnTanda & { name: string; message: string }>,
  };
  for (let i = 0; i < lote.length; i += TAMANYO_TANDA) {
    const trozo = lote.slice(i, i + TAMANYO_TANDA);
    const parcial = await api.importRecipes({ items: trozo.map((t) => t.item) });
    out.creados += parcial.creados;
    out.actualizados += parcial.actualizados;
    for (const e of parcial.errores) {
      const origen = trozo[e.index];
      if (origen) out.fallidos.push({ ...origen, name: e.name, message: e.message });
    }
  }
  return out;
}

export function useImportRecipes(): UseMutationResult<BatchResult, Error, RecipeBatch> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (batch: RecipeBatch) => {
      const todas: RecetaEnTanda[] = batch.items.map((item, indice) => ({ item, indice }));
      const primera = await enviarRecetas(todas);
      const total: BatchResult = {
        creados: primera.creados,
        actualizados: primera.actualizados,
        errores: [],
      };

      /*
       * Segunda vuelta con las que han fallado.
       *
       * El fichero se parte en tandas de 200, y una receta puede apuntar a una
       * subreceta que caia en una tanda POSTERIOR: cuando se intento, esa
       * subreceta todavia no existia. El servidor no la deja a medias —no
       * escribe nada si no puede enlazarlo todo—, asi que reintentarla cuando
       * ya esta el fichero entero es seguro y no duplica nada.
       *
       * Solo si hubo mas de una tanda: dentro de una, el servidor ya resuelve
       * el orden el solo y reintentar seria pedirle lo mismo dos veces.
       */
      let fallidos = primera.fallidos;
      if (fallidos.length > 0 && todas.length > TAMANYO_TANDA) {
        const segunda = await enviarRecetas(fallidos.map(({ item, indice }) => ({ item, indice })));
        total.creados += segunda.creados;
        total.actualizados += segunda.actualizados;
        fallidos = segunda.fallidos;
      }

      total.errores = fallidos
        .map((f) => ({ index: f.indice, name: f.name, message: f.message }))
        .sort((a, b) => a.index - b.index);
      return total;
    },
    onSuccess: () => invalidateCosting(queryClient),
  });
}

/* ------------------------------------------------------------------ */
/* Panel de plataforma (solo administradores)                          */
/* ------------------------------------------------------------------ */

export function useAdminOverview(): UseQueryResult<AdminOverview> {
  return useQuery({
    queryKey: keys.adminOverview,
    queryFn: () => api.getAdminOverview(),
    retry: false,
    staleTime: 60_000,
  });
}

export function useAdminChangePlan(): UseMutationResult<
  { ok: true },
  Error,
  { tenantId: string; plan: 'basic' | 'pro' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, plan }) => api.adminChangePlan(tenantId, plan),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.adminOverview });
      // El plan lo fija el webhook; segunda pasada cuando haya llegado.
      setTimeout(() => void qc.invalidateQueries({ queryKey: keys.adminOverview }), 4000);
    },
  });
}

export function useAdminApplyCoupon(): UseMutationResult<
  { ok: true },
  Error,
  { tenantId: string; coupon: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, coupon }) => api.adminApplyCoupon(tenantId, coupon),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.adminOverview }),
  });
}

const invalidaPnl = (qc: ReturnType<typeof useQueryClient>) =>
  void qc.invalidateQueries({ queryKey: ['admin-pnl'] });

export function useAdminEnviarResumenSemanal(): UseMutationResult<
  { enviado: boolean; destinatarios: string[]; asunto: string },
  Error,
  void
> {
  return useMutation({ mutationFn: () => api.adminEnviarResumenSemanal() });
}

export function useAdminRetryHolded(): UseMutationResult<
  { resultados: HoldedReintento[] },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId) => api.adminRetryHolded(tenantId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.adminOverview }),
  });
}

export function useAdminSetInternal(): UseMutationResult<
  { ok: true; internal: boolean },
  Error,
  { tenantId: string; internal: boolean }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, internal }) => api.adminSetInternal(tenantId, internal),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.adminOverview });
      invalidaPnl(qc);
    },
  });
}

export function useAdminSetBonusLocations(): UseMutationResult<
  { ok: true; bonusLocations: number },
  Error,
  { tenantId: string; bonusLocations: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ tenantId, bonusLocations }) => api.adminSetBonusLocations(tenantId, bonusLocations),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.adminOverview }),
  });
}

export function usePlatformPnl(year: number): UseQueryResult<PlatformPnlResponse> {
  return useQuery({
    queryKey: keys.adminPnl(year),
    queryFn: () => api.getPlatformPnl(year),
    retry: false,
    staleTime: 60_000,
  });
}

export function useCreatePlatformExpense(): UseMutationResult<
  PlatformExpense,
  Error,
  PlatformExpenseInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.createPlatformExpense(input),
    onSuccess: (_, input) =>
      void qc.invalidateQueries({ queryKey: keys.adminPnl(Number(input.month.slice(0, 4))) }),
  });
}

export function useCreatePlatformRecurring(): UseMutationResult<
  PlatformRecurringExpense,
  Error,
  PlatformRecurringExpenseInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.createPlatformRecurring(input),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useDeletePlatformRecurring(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.deletePlatformRecurring(id),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useSetPlatformSettings(): UseMutationResult<PlatformSettings, Error, PlatformSettings> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => api.setPlatformSettings(input),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useSetPlatformSettlement(): UseMutationResult<
  unknown,
  Error,
  { month: string; done: boolean; note?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, ...input }) => api.setPlatformSettlement(month, input),
    onSuccess: () => invalidaPnl(qc),
  });
}

export function useDeletePlatformExpense(): UseMutationResult<
  void,
  Error,
  { month: string; expenseId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ month, expenseId }) => api.deletePlatformExpense(month, expenseId),
    onSuccess: (_, { month }) =>
      void qc.invalidateQueries({ queryKey: keys.adminPnl(Number(month.slice(0, 4))) }),
  });
}

/* ------------------------------------------------------------------ */
/* Integraciones con TPV                                               */
/* ------------------------------------------------------------------ */

export function usePosIntegrations(): UseQueryResult<PosIntegrationsResponse> {
  return useQuery({ queryKey: keys.posIntegrations, queryFn: () => api.listPosIntegrations() });
}

export function useStartPosConnection(): UseMutationResult<{ url: string }, Error, PosProvider> {
  return useMutation({ mutationFn: (provider) => api.startPosConnection(provider) });
}

export function useCompletePosConnection(): UseMutationResult<
  PosIntegrationView,
  Error,
  { provider: PosProvider; code: string; state: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, code, state }) => api.completePosConnection(provider, { code, state }),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useConnectLoyverseToken(): UseMutationResult<PosIntegrationView, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token) => api.connectLoyverseToken(token),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useUpdatePosIntegration(): UseMutationResult<
  PosIntegrationView,
  Error,
  { provider: PosProvider; connectionId: string; patch: PosIntegrationPatch }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, connectionId, patch }) => api.updatePosIntegration(provider, connectionId, patch),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

/** Trae las ventas del rango al libro de ingresos. */
export function useSyncPosSales(): UseMutationResult<
  PosSyncResult,
  Error,
  { provider: PosProvider; connectionId: string; period: Period }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, connectionId, period }) => api.syncPosSales(provider, connectionId, period),
    onSettled: () => {
      invalidateFinance(qc);
      invalidateAll(qc, [['pos-integrations'], ['dashboard'], ['kpis'], ['weekday']]);
    },
  });
}

/** Vuelca las unidades vendidas del periodo a la carta (menu engineering). */
export function useApplyPosUnits(): UseMutationResult<
  PosUnitsResult,
  Error,
  { provider: PosProvider; period: Period }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, period }) => api.applyPosUnits(provider, period),
    onSettled: () => invalidateAll(qc, [['menu-items'], ['pricing'], ['dashboard'], ['kpis']]),
  });
}

export function useDisconnectPos(): UseMutationResult<
  void,
  Error,
  { provider: PosProvider; connectionId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, connectionId }) => api.disconnectPos(provider, connectionId),
    onSuccess: () => invalidateAll(qc, [['pos-integrations']]),
  });
}

export function useEstimateMissingPrices() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => api.estimateMissingPrices(), onSuccess: () => invalidateCosting(qc) });
}

export function useDrafts() {
  const { locationId } = useLocationScope();
  return useQuery({ queryKey: ['drafts', 'list', locationId], queryFn: () => api.listDrafts(locationId) });
}
export function useDraft(id: string) {
  return useQuery({ queryKey: ['drafts', id], queryFn: () => api.getDraft(id), enabled: Boolean(id) });
}
export function useDiscardDraft() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => api.discardDraft(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['drafts'] }) });
}
